import global from "@/prototype/global.js";
import {get_translation} from "svg-utils";

import svgson from "svgson";

import uuid from "@/library/uuid.js";

const d3 = global.d3;
const log = global.log.getLogger("f_mtac.scene.object.svg.generic");
log.setLevel(log.levels.WARN);

const generate_object_id = () => `local-${uuid()}`;

const serialize_node = (handle, layer_name) => {
    var node_html = handle.html();
    var nodes_data = svgson.parse(node_html);
    if (nodes_data.length === 0) {
        log.error("empty node with", {node_html});
        return {};
    }
    if (nodes_data.length > 1) {
        log.warn("more than one node with", {node_html, nodes_data});
    }
    var [node_data] = nodes_data;
    node_data.layer_name = layer_name;
    return node_data;
};

const deserialize_node = (handle, layer_name, node_data) => {
    if (node_data.type !== "element") {
        log.error("trying to deserialize a wrong type for svg layer", node_data.type);
        return;
    }
    if (node_data.layer_name !== layer_name) {
        log.error("deserialized on a wrong a layer", node_data.layer_name, "!=", layer_name);
        return;
    }

    if (layer_name == "notes") {
        // AGPOC-3245 moving a postit with special character chenges its content
        // example: "&" becomes "&amp;" this is the escape notation for HTML
        // Fix: unescape the special characters when deserializing
        let str = svgson.stringify([node_data]);
        str = str.replaceAll("&amp;", "&");
        str = str.replaceAll("&gt;", ">");
        str = str.replaceAll("&lt;", "<");
        //log.debug("deserialize_node after str =", str);
        handle.html(str);
    } else {
        handle.html(svgson.stringify([node_data]));
    }
};

const error_property = () => {
    log.error("read only!!!");
};

const Generic = function (group, data) {
    if (!("handle" in data)) {
        data.handle = d3.create("svg:g").attr("class", "handle");
    } else {
        data.id = data.handle.attr("id");
    }
    if (!("id" in data)) {
        data.id = generate_object_id();
    }
    data.handle.attr("id", data.id);
    Object.defineProperties(this, {
        id: {
            set: error_property,
            get: () => data.id
        },
        handle: {
            set: error_property,
            get: () => data.handle
        },
        node: {
            set: error_property,
            get: () => data.handle.select("g.node")
        },
        layer_name: {
            set: error_property,
            get: () => group.attr("class")
        },
        type: {
            set: error_property,
            get: () => "element"
        }
    });
    this.change_id = () => {
        data.id = generate_object_id();
        data.handle.attr("id", data.id);
    };
    group.node().appendChild(data.handle.node());
    if ("node_data" in data) {
        deserialize_node(data.handle, this.layer_name, data.node_data);
        delete data.node_data;
    }
    if (this.node.empty()) {
        this.position = null;
        this.node_transform = null;
    } else {
        this.position = get_position(data.handle.node());
        this.node_transform = get_translation(this.node.attr("transform"));
    }
};

const get_position = (element) => {
    try {
        // getBBox is evil, we shall probably find a way to avoid this
        var p = element.getBBox();
    } catch (error) {
        log.error("getBBox failed");
        return null;
    }
    if (p !== null && p.x === 0 && p.y === 0 && p.width === 0 && p.height === 0) {
        log.error("getBBox return a null position/size");
        return null;
    }
    let {x, y, width, height} = p;
    return {x, y, width, height};
};

Generic.prototype.update_position = function () {
    this.position = get_position(this.handle.node());
    this.node_transform = get_translation(this.node.attr("transform"));
    log.info("compare between position", this.position, "transform", this.node_transform);
};

Generic.prototype.import = function () {
    log.error("the import method is deprecated and no longer functional");
};

Generic.prototype.serialize = function () {
    return serialize_node(this.handle, this.layer_name);
};

Generic.prototype.deserialize = function (node_data) {
    deserialize_node(this.handle, this.layer_name, node_data);
    this.update_position();
};

Generic.prototype.transform = function (new_transform) {
    Object.assign(this.node_transform, new_transform);
    this.node.attr("transform", `translate(${this.node_transform.x},${this.node_transform.y})`);
    this.update_position();
};
Generic.prototype.move_to = function ({x, y}) {
    this.transform({x, y});
};
Generic.prototype.move = function ({x, y}) {
    this.transform({
        x: this.node_transform.x + x,
        y: this.node_transform.y + y
    });
};
Generic.prototype.distance = function ({x, y}) {
    return Math.sqrt(Math.pow(x - this.node_transform.x, 2) + Math.pow(y - this.node_transform.y, 2));
};
Generic.prototype.center = function (selector) {
    this.position = get_position(this.handle.node());
    if (selector !== undefined) {
        let base_element = this.handle.select(selector).node();
        // getBBox is evil, we shall probably find a way to avoid this
        let {x, y, width, height} = base_element.getBBox();
        let relative_position = {x: this.position.x + x + width / 2, y: this.position.y + y + height / 2};
        let element = base_element;
        while (element.parentNode !== null && element.parentNode !== this.handle.node()) {
            let {x, y} = element.parentNode.getBBox();
            relative_position.x -= x;
            relative_position.y -= y;
            element = element.parentNode;
        }
        return relative_position;
    }
    return {
        x: this.position.x + this.position.width / 2,
        y: this.position.y + this.position.height / 2
    };
};
Generic.prototype.top = function () {
    var handle = this.handle.node();
    var layer = handle.parentNode;
    layer.appendChild(handle);
};

export default Generic;
